import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '../../ui';

// Mapbox access token from environment
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Default country bias - South Africa
const DEFAULT_COUNTRY = 'za'; // ISO 3166-1 alpha-2 code for South Africa
const COUNTRY_BBOX = '16.3,-35.0,33.0,-22.0'; // South Africa bounding box for proximity bias

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

const SmartAddressInput = ({ value, onChange, onSelect, placeholder = 'Start typing an address...' }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const abortControllerRef = useRef(null);

  const debouncedQuery = useDebounce(query, 300);

  // Fetch suggestions from Mapbox Geocoding API
  const fetchSuggestions = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3 || !MAPBOX_TOKEN) {
      setSuggestions([]);
      return;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);

    try {
      // Mapbox Geocoding API - Prioritized for South Africa
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json?` +
        `access_token=${MAPBOX_TOKEN}` +
        `&country=${DEFAULT_COUNTRY}` +  // Limit to South Africa
        `&types=address,place,locality,neighborhood` +
        `&limit=6` +
        `&language=en` +
        `&bbox=${COUNTRY_BBOX}`,  // Bounding box for South Africa
        {
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error('Mapbox API request failed');
      }

      const data = await response.json();
      
      const formatted = data.features.map((feature) => {
        // Parse context for city, province, country
        const context = feature.context || [];
        const getContext = (type) => {
          const item = context.find(c => c.id.startsWith(type));
          return item ? item.text : '';
        };

        return {
          id: feature.id,
          displayName: feature.place_name,
          address: feature.text + (feature.address ? ` ${feature.address}` : ''),
          fullAddress: feature.place_name.split(',')[0],
          city: getContext('place') || getContext('locality') || '',
          province: getContext('region') || '',
          postalCode: getContext('postcode') || '',
          country: getContext('country') || '',
          coordinates: feature.center,
        };
      });
      
      setSuggestions(formatted);
      if (formatted.length > 0) {
        setShowSuggestions(true);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Mapbox geocoding error:', err);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Effect to fetch suggestions when debounced query changes
  useEffect(() => {
    if (debouncedQuery && debouncedQuery.length >= 3) {
      fetchSuggestions(debouncedQuery);
    } else {
      setSuggestions([]);
    }
  }, [debouncedQuery, fetchSuggestions]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange(newValue);
    setSelectedIndex(-1);
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    setQuery(suggestion.fullAddress);
    onChange(suggestion.fullAddress);
    onSelect?.({
      address: suggestion.fullAddress,
      city: suggestion.city,
      province: suggestion.province,
      postalCode: suggestion.postalCode,
      country: suggestion.country,
    });
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  // Update query when value prop changes externally
  useEffect(() => {
    if (value !== undefined && value !== query) {
      setQuery(value || '');
    }
  }, [value]);

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Input
          label="Address"
          value={query}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
        />
        
        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute right-3 top-9 flex items-center gap-2">
            <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        )}
      </div>

      {/* Suggestions dropdown */}
      <AnimatePresence>
        {showSuggestions && suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <ul className="max-h-72 overflow-y-auto">
              {suggestions.map((suggestion, index) => (
                <li key={suggestion.id || index}>
                  <button
                    type="button"
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className={`
                      w-full text-left px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors
                      ${index === selectedIndex ? 'bg-blue-50 dark:bg-blue-900/30' : ''}
                      ${index !== suggestions.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''}
                    `}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div className="overflow-hidden flex-1">
                        <div className="font-medium text-gray-900 dark:text-white truncate">
                          {suggestion.fullAddress}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {[suggestion.city, suggestion.province, suggestion.country]
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            <div className="px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm5.696 14.943c-4.103 4.103-11.433 2.794-11.433 2.794S4.954 10.407 9.057 6.304C11.2 4.161 14.74 4.161 16.883 6.304s2.162 5.488-.019 7.631l-1.168 1.008z"/>
                </svg>
                Powered by Mapbox
              </span>
              <span>↑↓ Navigate • Enter Select</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hint text */}
      <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
        🇿🇦 Type at least 3 characters for South African address suggestions
      </p>
    </div>
  );
};

export default SmartAddressInput;
